import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import FeedbackPopup from '../components/FeedbackPopup';
import { SEEDED_WAITLIST_COUNT, BACKEND_URL } from '../constants';
import { 
  Shield, 
  TrendingDown, 
  CreditCard, 
  Bell, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  DollarSign,
  Zap,
  Lock,
  Eye,
  FileText,
  BarChart3,
  Award,
  TrendingUp,
  Users,
  Target,
  Calendar,
  Lightbulb,
  Activity
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [priorityProblem, setPriorityProblem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(SEEDED_WAITLIST_COUNT);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Helper function to get personalized confirmation message
  const getPersonalizedMessage = (problem) => {
    const messages = {
      'trial_reminder': {
        title: 'Thanks for joining!',
        message: "You're on the list to help us stop surprise subscription charges."
      },
      'bill_increase': {
        title: 'Thanks for joining!',
        message: "You're on the list to help us catch silent price increases automatically."
      },
      'credit_protection': {
        title: 'Thanks for joining!',
        message: "You're on the list to help us build proactive credit score protection."
      },
      'reduce_interest': {
        title: 'Thanks for joining!',
        message: "You're on the list to help us reduce unnecessary credit card interest."
      },
      'find_subscriptions': {
        title: 'Thanks for joining!',
        message: "You're on the list to help us find hidden subscriptions and recurring charges."
      },
      'continuous_monitoring': {
        title: 'Thanks for joining!',
        message: "You're on the list to help us build a proactive financial watchdog."
      }
    };
    return messages[problem] || {
      title: 'Thank you for joining!',
      message: "We'll be in touch soon with early access details."
    };
  };

  // Fetch actual waitlist count and retry pending submissions
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waitlist/count`);
        const data = await response.json();
        setWaitlistCount(SEEDED_WAITLIST_COUNT + data.count);
      } catch (error) {
        // Keep the default seeded value
      }
    };
    
    const retryPendingSubmissions = async () => {
      const pendingWaitlist = JSON.parse(localStorage.getItem('pendingWaitlist') || '[]');
      const pendingFeedback = JSON.parse(localStorage.getItem('pendingFeedback') || '[]');
      
      // Retry waitlist submissions
      if (pendingWaitlist.length > 0) {
        const successfulSubmissions = [];
        
        for (const submission of pendingWaitlist) {
          try {
            const response = await fetch(`${BACKEND_URL}/api/waitlist`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(submission),
            });
            
            if (response.ok) {
              successfulSubmissions.push(submission);
              
              if (window.posthog) {
                window.posthog.capture('waitlist_retry_success', {
                  email: submission.email,
                });
              }
            }
          } catch (error) {
            // Retry failed, will try again on next page load
          }
        }
        
        // Remove successful submissions from localStorage
        if (successfulSubmissions.length > 0) {
          const remaining = pendingWaitlist.filter(
            sub => !successfulSubmissions.some(s => s.email === sub.email)
          );
          localStorage.setItem('pendingWaitlist', JSON.stringify(remaining));
        }
      }
      
      // Retry feedback submissions
      if (pendingFeedback.length > 0) {
        const successfulFeedback = [];
        
        for (const feedback of pendingFeedback) {
          try {
            const response = await fetch(`${BACKEND_URL}/api/feedback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(feedback),
            });
            
            if (response.ok) {
              successfulFeedback.push(feedback);
            }
          } catch (error) {
            // Retry failed, will try again on next page load
          }
        }
        
        // Remove successful submissions
        if (successfulFeedback.length > 0) {
          const remaining = pendingFeedback.filter(
            (_, index) => !successfulFeedback.includes(pendingFeedback[index])
          );
          localStorage.setItem('pendingFeedback', JSON.stringify(remaining));
        }
      }
    };
    
    fetchWaitlistCount();
    retryPendingSubmissions();
  }, []); // Empty dependency - runs once on mount

  // Handle demo click and set localStorage flag
  const handleDemoClick = useCallback((url) => {
    localStorage.setItem('demoOpened', 'true');
    
    // Track analytics
    if (window.posthog) {
      window.posthog.capture('demo_clicked');
    }
    
    window.open(url, '_blank');
  }, []);

  // Check for demo feedback on multiple events
  useEffect(() => {
    let timeoutId;
    let intervalId;
    
    const showPopupIfNeeded = (source = 'unknown') => {
      const demoOpened = localStorage.getItem('demoOpened');
      
      if (demoOpened === 'true' && !showFeedbackPopup) {
        if (timeoutId) clearTimeout(timeoutId);
        
        timeoutId = setTimeout(() => {
          setShowFeedbackPopup(true);
          
          if (window.posthog) {
            window.posthog.capture('demo_feedback_popup_shown', {
              trigger_source: source
            });
          }
          
          localStorage.removeItem('demoOpened');
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        showPopupIfNeeded('visibilitychange');
      }
    };

    const handleFocus = () => {
      showPopupIfNeeded('focus');
    };
    
    const handlePageShow = (event) => {
      showPopupIfNeeded('pageshow');
    };
    
    const handleMouseMove = () => {
      showPopupIfNeeded('mousemove');
      document.removeEventListener('mousemove', handleMouseMove);
      
      setTimeout(() => {
        document.addEventListener('mousemove', handleMouseMove, { once: true });
      }, 5000);
    };

    // Add all event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('mousemove', handleMouseMove, { once: true });
    
    // Check on mount
    showPopupIfNeeded('mount');
    
    // Interval check every 2 seconds while tab is active
    intervalId = setInterval(() => {
      if (!document.hidden) {
        const demoOpened = localStorage.getItem('demoOpened');
        if (demoOpened === 'true' && !showFeedbackPopup) {
          showPopupIfNeeded('interval');
        }
      }
    }, 2000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('mousemove', handleMouseMove);
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [showFeedbackPopup]);

  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit feedback');
      }
      
      // Track with PostHog
      if (window.posthog) {
        window.posthog.capture('feedback_submitted_success');
      }
    } catch (error) {
      // Track error with PostHog
      if (window.posthog) {
        window.posthog.capture('feedback_submission_failed', {
          error: error.message
        });
      }
    }
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const submissionData = {
      email,
      country: country || null,
      priority_problem: priorityProblem,
      timestamp: new Date().toISOString(),
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      // Success - API call worked!
      setIsSubmitting(false);
      setWaitlistCount(prev => prev + 1);
      
      // Track with PostHog
      if (window.posthog) {
        window.posthog.capture('waitlist_joined', {
          has_country: !!country,
          priority_problem: priorityProblem,
        });
      }
      
      // Redirect to survey with user data
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/',
          source_campaign: 'homepage',
          priority_problem: priorityProblem
        }
      });
      
    } catch (error) {
      // Backend is down - save to localStorage as backup
      const pendingSubmissions = JSON.parse(localStorage.getItem('pendingWaitlist') || '[]');
      pendingSubmissions.push(submissionData);
      localStorage.setItem('pendingWaitlist', JSON.stringify(pendingSubmissions));
      
      setIsSubmitting(false);
      
      // Track offline submission
      if (window.posthog) {
        window.posthog.capture('waitlist_joined_offline', {
          saved_to_backup: true,
          priority_problem: priorityProblem,
        });
      }
      
      // Redirect to survey even for offline signups
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/',
          source_campaign: 'homepage',
          priority_problem: priorityProblem
        }
      });
    }
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkles className="icon-small" />
              <span>AI-Powered Financial Guardian</span>
            </div>
            
            <h1 className="hero-title">
              Stop Losing Up to $1,400 a Year to Hidden Financial Leaks
            </h1>
            
            <p className="hero-subtitle">
              An AI watchdog that continuously protects your finances from hidden leaks, unnecessary interest, 
              and costly mistakes — detecting savings opportunities and recommending the next best action.
            </p>
            
            <p className="hero-differentiation">
              Unlike one-time subscription trackers, this system continuously monitors your entire financial activity 
              to detect hidden money leaks — subscriptions, bill increases, credit card interest, utilization risks, 
              duplicate services, and missed financial opportunities.
            </p>
            
            <div className="hero-cta">
              <Button size="lg" className="cta-primary" onClick={() => {
                document.getElementById('waitlist-section').scrollIntoView({ behavior: 'smooth' });
              }}>
                Join Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="cta-secondary"
                onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
              >
                Try Interactive Demo
              </Button>
            </div>
            
            <div className="hero-estimate">
              <BarChart3 className="estimate-icon" />
              <p>Estimated annual financial leaks detected for typical households: <strong>$300–$1,400</strong></p>
            </div>
            
            <div className="hero-trust">
              <Shield className="trust-icon" />
              <p>Privacy-first. Secure by design. Your data is never sold.</p>
            </div>
          </div>
          
          <div className="hero-visual">
            <div className="dashboard-mock">
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80" 
                alt="Financial Dashboard"
                className="dashboard-image"
              />
              <div className="insight-cards">
                <Card className="insight-card insight-1">
                  <CardContent className="insight-content">
                    <div className="insight-icon savings">
                      <TrendingDown className="h-5 w-5" />
                    </div>
                    <div className="insight-text">
                      <p className="insight-label">Potential Savings</p>
                      <p className="insight-value">$420/year</p>
                      <p className="insight-detail">in subscriptions</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="insight-card insight-2">
                  <CardContent className="insight-content">
                    <div className="insight-icon alert">
                      <AlertCircle className="h-5 w-5" />
                    </div>
                    <div className="insight-text">
                      <p className="insight-label">Bill Increase</p>
                      <p className="insight-value">+18%</p>
                      <p className="insight-detail">Internet bill</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="insight-card insight-3">
                  <CardContent className="insight-content">
                    <div className="insight-icon optimize">
                      <Award className="h-5 w-5" />
                    </div>
                    <div className="insight-text">
                      <p className="insight-label">Card Rewards</p>
                      <p className="insight-value">+$180</p>
                      <p className="insight-detail">Using Card B</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Statement */}
      <section className="positioning-section">
        <div className="positioning-container">
          <h2 className="positioning-title">Not a dashboard. A proactive financial assistant.</h2>
          <p className="positioning-subtitle">
            Instead of showing charts, the system actively detects financial leaks and alerts you before they cost you money.
          </p>
        </div>
      </section>

      {/* Value Section */}
      <section className="value-section">
        <div className="section-container">
          <h2 className="section-title">Where Your Money Quietly Leaks</h2>
          <p className="section-subtitle">Without even realizing it, money quietly slips away</p>
          
          <div className="value-grid">
            <Card className="value-card">
              <CardContent className="value-content">
                <div className="value-icon">
                  <TrendingDown className="h-6 w-6" />
                </div>
                <h3 className="value-title">Subscriptions you forgot</h3>
                <p className="value-amount">$180/year</p>
              </CardContent>
            </Card>
            
            <Card className="value-card">
              <CardContent className="value-content">
                <div className="value-icon">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <h3 className="value-title">Price increases on bills</h3>
                <p className="value-amount">$240/year</p>
              </CardContent>
            </Card>
            
            <Card className="value-card">
              <CardContent className="value-content">
                <div className="value-icon">
                  <CreditCard className="h-6 w-6" />
                </div>
                <h3 className="value-title">Credit card interest leakage</h3>
                <p className="value-amount">$800/year</p>
              </CardContent>
            </Card>
            
            <Card className="value-card">
              <CardContent className="value-content">
                <div className="value-icon">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="value-title">Duplicate services</h3>
                <p className="value-amount">$120/year</p>
              </CardContent>
            </Card>
          </div>
          
          <Card className="savings-total-card">
            <CardContent className="savings-total-content">
              <div className="savings-total-header">
                <DollarSign className="savings-icon" />
                <h3>Total Potential Savings</h3>
              </div>
              <p className="savings-total-amount">$300 – $1,400</p>
              <p className="savings-total-period">per year</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 360° Financial Leak Detection Section */}
      <section className="leak-detection-section">
        <div className="section-container">
          <h2 className="section-title">360° Financial Leak Detection</h2>
          <p className="section-subtitle">
            Most tools focus on only one financial problem — like subscriptions or budgeting. This system continuously 
            scans across your financial activity to detect hidden money leaks and missed opportunities.
          </p>
          
          <div className="leak-categories-grid">
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon trials">
                  <Bell className="h-7 w-7" />
                </div>
                <h3 className="category-title">Free Trial Conversions</h3>
                <p className="category-description">
                  Alert before a free trial becomes a paid subscription
                </p>
              </CardContent>
            </Card>
            
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon utilization">
                  <Activity className="h-7 w-7" />
                </div>
                <h3 className="category-title">Credit Utilization Risks</h3>
                <p className="category-description">
                  Detect high credit card utilization that could lower your credit score
                </p>
              </CardContent>
            </Card>
            
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon smart-card">
                  <CreditCard className="h-7 w-7" />
                </div>
                <h3 className="category-title">Smart Card Usage</h3>
                <p className="category-description">
                  Recommend which credit card to use to reduce interest and maximize rewards
                </p>
              </CardContent>
            </Card>
            
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon opportunities">
                  <Sparkles className="h-7 w-7" />
                </div>
                <h3 className="category-title">Missed Financial Opportunities</h3>
                <p className="category-description">
                  Detect situations where small financial actions could save money
                </p>
              </CardContent>
            </Card>
            
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon insurance">
                  <Shield className="h-7 w-7" />
                </div>
                <h3 className="category-title">Insurance Claim Opportunities</h3>
                <p className="category-description">
                  Detect transactions that may qualify for insurance reimbursement
                </p>
              </CardContent>
            </Card>
            
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon duplicates">
                  <AlertCircle className="h-7 w-7" />
                </div>
                <h3 className="category-title">Subscription Overlaps Across Accounts</h3>
                <p className="category-description">
                  Identify duplicate services across family accounts
                </p>
              </CardContent>
            </Card>
            
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon timing">
                  <BarChart3 className="h-7 w-7" />
                </div>
                <h3 className="category-title">Payment Timing Optimization</h3>
                <p className="category-description">
                  Detect when changing payment timing could reduce credit card interest
                </p>
              </CardContent>
            </Card>
            
            <Card className="leak-category-card">
              <CardContent className="leak-category-content">
                <div className="category-icon admin">
                  <FileText className="h-7 w-7" />
                </div>
                <h3 className="category-title">Financial Deadlines & Life Admin</h3>
                <p className="category-description">
                  Track passport renewals, insurance policies, and key financial documents
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="leak-detection-footer">
            <div className="footer-badge">
              <Target className="badge-icon" />
              <span>Complete Financial Coverage</span>
            </div>
            <p className="footer-text">
              This comprehensive approach catches money leaks that single-purpose tools miss
            </p>
          </div>
        </div>
      </section>

      {/* Trial-to-Paid Alert Section */}
      <section className="trial-alert-section">
        <div className="section-container">
          <h2 className="section-title">Stop Forgetting to Cancel Free Trials</h2>
          <p className="section-description">
            Free trials are one of the most common ways people lose money.
          </p>
          <p className="section-description">
            The system detects trial-based subscriptions and alerts you before they convert into paid plans — 
            so you can cancel before the charge happens.
          </p>
          
          {/* Alert Card Example */}
          <div className="trial-alert-demo">
            <Card className="trial-alert-card">
              <CardContent className="trial-alert-content">
                <div className="alert-header">
                  <Bell className="alert-bell-icon" />
                  <span className="alert-badge">Free Trial Ending Soon</span>
                </div>
                
                <div className="alert-body">
                  <h3 className="alert-service">Adobe Creative Cloud</h3>
                  <div className="alert-details">
                    <div className="alert-detail-item">
                      <Calendar className="alert-icon-small" />
                      <span>Renews in 2 days</span>
                    </div>
                    <div className="alert-detail-item">
                      <DollarSign className="alert-icon-small" />
                      <span>Next charge: $29.99</span>
                    </div>
                  </div>
                </div>
                
                <Button className="alert-action-button" variant="outline">
                  Cancel Now
                </Button>
              </CardContent>
            </Card>
          </div>
          
          {/* Notification System */}
          <div className="notification-system">
            <h3 className="notification-title">The system ensures you never miss important events with:</h3>
            <div className="notification-methods">
              <div className="notification-method">
                <Bell className="notification-icon" />
                <span>Push notifications</span>
              </div>
              <div className="notification-method">
                <FileText className="notification-icon" />
                <span>Email alerts</span>
              </div>
              <div className="notification-method">
                <Sparkles className="notification-icon" />
                <span>In-app reminders</span>
              </div>
            </div>
            
            <div className="notification-timing">
              <h4 className="timing-title">Alerts are triggered before charges:</h4>
              <ul className="timing-list">
                <li>✓ 3 days before renewal</li>
                <li>✓ 1 day before renewal</li>
              </ul>
            </div>
          </div>
          
          <div className="trial-hook">
            <AlertCircle className="hook-icon" />
            <p className="hook-text">Get alerted before free trials turn into charges.</p>
          </div>
        </div>
      </section>

      {/* Why Continuous Monitoring Matters Section */}
      <section className="monitoring-matters-section">
        <div className="section-container">
          <div className="monitoring-intro">
            <Activity className="monitoring-intro-icon" />
            <h2 className="section-title">Why Continuous Monitoring Matters</h2>
            <p className="monitoring-intro-text">
              Financial discipline isn't enough. Even the most careful people face ongoing financial risks that 
              appear over time — often without realizing it until money is already lost.
            </p>
          </div>
          
          {/* Challenge Cards - Redesigned to Match Page Style */}
          <div className="monitoring-challenges-grid">
            <Card className="monitoring-challenge-card">
              <CardContent className="monitoring-challenge-content">
                <div className="challenge-visual">
                  <Bell className="challenge-large-icon" />
                </div>
                <h3 className="challenge-title">Free Trial Amnesia</h3>
                <p className="challenge-description">
                  Sign up, forget to cancel, pay $30–$50 for unused service
                </p>
                <div className="challenge-impact">
                  <span className="impact-value">$35</span>
                  <span className="impact-label">per forgotten trial</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="monitoring-challenge-card">
              <CardContent className="monitoring-challenge-content">
                <div className="challenge-visual">
                  <TrendingUp className="challenge-large-icon" />
                </div>
                <h3 className="challenge-title">Silent Price Increases</h3>
                <p className="challenge-description">
                  Bills quietly increase 10–20% without alerts
                </p>
                <div className="challenge-impact">
                  <span className="impact-value">$240</span>
                  <span className="impact-label">annually</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="monitoring-challenge-card">
              <CardContent className="monitoring-challenge-content">
                <div className="challenge-visual">
                  <CreditCard className="challenge-large-icon" />
                </div>
                <h3 className="challenge-title">Credit Score Damage</h3>
                <p className="challenge-description">
                  Utilization crosses 30%, score drops, rates increase
                </p>
                <div className="challenge-impact">
                  <span className="impact-value">$30,000+</span>
                  <span className="impact-label">long-term cost</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="monitoring-challenge-card">
              <CardContent className="monitoring-challenge-content">
                <div className="challenge-visual">
                  <Eye className="challenge-large-icon" />
                </div>
                <h3 className="challenge-title">Monitoring Fatigue</h3>
                <p className="challenge-description">
                  Stop checking regularly, miss duplicate charges
                </p>
                <div className="challenge-impact">
                  <span className="impact-value">$120</span>
                  <span className="impact-label">quarterly</span>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Value Statement */}
          <div className="monitoring-value-statement">
            <Shield className="value-statement-icon" />
            <div className="value-statement-content">
              <h3 className="value-statement-title">Continuous Protection Solves This</h3>
              <p className="value-statement-text">
                The system continuously watches for new financial leaks and alerts you before they become costly — 
                so you stay protected even when life gets busy.
              </p>
            </div>
          </div>
          
          {/* Continuous Monitoring Visual Timeline */}
          <div className="monitoring-timeline">
            <div className="timeline-header">
              <Activity className="timeline-icon" />
              <h3 className="timeline-title">How Continuous Protection Works Over Time</h3>
              <p className="timeline-subtitle">
                Real examples of alerts you'd receive in a typical 45-day period
              </p>
            </div>
            
            <div className="timeline-track">
              <div className="timeline-item">
                <div className="timeline-dot active"></div>
                <div className="timeline-content">
                  <div className="timeline-badge">Day 1</div>
                  <p className="timeline-text">Trial started detected</p>
                  <span className="timeline-detail">Netflix 30-day trial begins</span>
                </div>
              </div>
              
              <div className="timeline-connector"></div>
              
              <div className="timeline-item">
                <div className="timeline-dot active"></div>
                <div className="timeline-content">
                  <div className="timeline-badge">Day 12</div>
                  <p className="timeline-text">Alert: Trial ending soon</p>
                  <span className="timeline-detail">2 days before $29.99 charge</span>
                </div>
              </div>
              
              <div className="timeline-connector"></div>
              
              <div className="timeline-item">
                <div className="timeline-dot active"></div>
                <div className="timeline-content">
                  <div className="timeline-badge">Day 30</div>
                  <p className="timeline-text">Bill increase detected</p>
                  <span className="timeline-detail">Internet bill up 15% ($12/mo)</span>
                </div>
              </div>
              
              <div className="timeline-connector"></div>
              
              <div className="timeline-item">
                <div className="timeline-dot active"></div>
                <div className="timeline-content">
                  <div className="timeline-badge">Day 45</div>
                  <p className="timeline-text">Credit utilization warning</p>
                  <span className="timeline-detail">Card A at 38% — rebalance suggested</span>
                </div>
              </div>
              
              <div className="timeline-connector ongoing"></div>
              
              <div className="timeline-item ongoing">
                <div className="timeline-dot pulse"></div>
                <div className="timeline-content">
                  <div className="timeline-badge ongoing">Always On</div>
                  <p className="timeline-text">Continuous monitoring</p>
                  <span className="timeline-detail">24/7 protection in background</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Key Message - Redesigned */}
          <div className="monitoring-conclusion">
            <div className="conclusion-icon-wrapper">
              <Target className="conclusion-icon" />
            </div>
            <div className="conclusion-content">
              <h3 className="conclusion-title">Financial leaks are not a one-time problem</h3>
              <p className="conclusion-text">
                New subscriptions start. Bills increase. Credit usage shifts. Financial optimization 
                opportunities appear and disappear. Monitoring should not be one-time either.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Score Protection Section */}
      <section className="credit-section">
        <div className="credit-container">
          <div className="credit-content">
            <h2 className="credit-title">Protect Your Credit Score and Avoid Higher Interest</h2>
            <p className="credit-subtitle">
              Small changes in credit card usage can impact your credit score.
            </p>
            
            <div className="credit-explanation">
              <p>
                If a single card crosses 30% utilization, your score may drop — even if you have available 
                credit on other cards.
              </p>
              
              <p className="credit-monitors">The system continuously monitors your balances and alerts you when:</p>
              
              <ul className="credit-list">
                <li>
                  <CheckCircle2 className="credit-check-icon" />
                  <span>One card is approaching high utilization</span>
                </li>
                <li>
                  <CheckCircle2 className="credit-check-icon" />
                  <span>Another card has unused limit</span>
                </li>
                <li>
                  <CheckCircle2 className="credit-check-icon" />
                  <span>A simple rebalance could protect your score</span>
                </li>
              </ul>
            </div>
            
            <Card className="credit-example-card">
              <CardContent className="credit-example-content">
                <div className="credit-example-header">
                  <AlertCircle className="credit-example-icon" />
                  <h4>Credit Utilization Warning</h4>
                </div>
                <div className="credit-example-details">
                  <div className="credit-card-status">
                    <span className="card-label">Card A:</span>
                    <span className="card-usage high">41% used</span>
                  </div>
                  <div className="credit-card-status">
                    <span className="card-label">Card B:</span>
                    <span className="card-usage low">8% used</span>
                  </div>
                </div>
                <div className="credit-suggestion">
                  <p className="suggestion-title">Suggested Action:</p>
                  <p className="suggestion-action">Shift $850 balance to Card B</p>
                </div>
                <div className="credit-impact">
                  <p className="impact-title">Impact:</p>
                  <p className="impact-result">Lower utilization and reduce credit score risk</p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="credit-visual">
            <Card className="credit-calculation-card">
              <CardContent className="credit-calculation-content">
                <h3 className="calculation-title">Long-Term Financial Impact</h3>
                <p className="calculation-description">
                  Credit utilization accounts for ~30% of your credit score. A small credit score drop can 
                  increase loan interest rates.
                </p>
                
                <div className="calculation-example">
                  <div className="calculation-row">
                    <span className="calculation-label">Mortgage amount:</span>
                    <span className="calculation-value">$400,000</span>
                  </div>
                  <div className="calculation-row">
                    <span className="calculation-label">Rate difference due to lower score:</span>
                    <span className="calculation-value highlight">0.5%</span>
                  </div>
                </div>
                
                <div className="calculation-result">
                  <p className="result-label">Potential extra interest over loan term:</p>
                  <p className="result-amount">$30,000+</p>
                </div>
                
                <p className="calculation-footer">
                  Even small credit score improvements can have major long-term impact.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Helps Section */}
      <section className="helps-section">
        <div className="section-container">
          <h2 className="section-title">Your Personal Financial Leak Detector</h2>
          <p className="section-subtitle">
            Instead of complex financial charts, the system highlights simple actions
          </p>
          
          <div className="helps-grid">
            <Card className="helps-card">
              <CardContent className="helps-content">
                <div className="helps-action">Cancel unused subscription</div>
                <div className="helps-arrow">→</div>
                <div className="helps-result">save $180/year</div>
              </CardContent>
            </Card>
            
            <Card className="helps-card">
              <CardContent className="helps-content">
                <div className="helps-action">Switch internet plan</div>
                <div className="helps-arrow">→</div>
                <div className="helps-result">save $240/year</div>
              </CardContent>
            </Card>
            
            <Card className="helps-card">
              <CardContent className="helps-content">
                <div className="helps-action">Move payment timing</div>
                <div className="helps-arrow">→</div>
                <div className="helps-result">reduce credit interest</div>
              </CardContent>
            </Card>
            
            <Card className="helps-card">
              <CardContent className="helps-content">
                <div className="helps-action">Submit insurance claim</div>
                <div className="helps-arrow">→</div>
                <div className="helps-result">recover eligible expenses</div>
              </CardContent>
            </Card>
          </div>
          
          <p className="helps-footer">
            The system continuously watches for these opportunities
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">Simple, secure, and automatic</p>
          
          <div className="how-grid">
            <Card className="how-card">
              <CardContent className="how-content">
                <div className="how-number">1</div>
                <div className="how-icon-container">
                  <Shield className="how-icon" />
                </div>
                <h3 className="how-step-title">Connect Your Accounts</h3>
                <p className="how-step-description">
                  Securely connect your bank accounts and credit cards
                </p>
              </CardContent>
            </Card>
            
            <Card className="how-card">
              <CardContent className="how-content">
                <div className="how-number">2</div>
                <div className="how-icon-container">
                  <Activity className="how-icon" />
                </div>
                <h3 className="how-step-title">Detect Financial Leaks</h3>
                <p className="how-step-description">
                  The system analyzes transaction patterns, subscription activity, bill history, and credit usage 
                  behavior to identify hidden financial inefficiencies
                </p>
                <p className="how-step-detail">
                  It continuously monitors your financial activity to detect patterns that indicate wasted money or 
                  missed opportunities
                </p>
              </CardContent>
            </Card>
            
            <Card className="how-card">
              <CardContent className="how-content">
                <div className="how-number">3</div>
                <div className="how-icon-container">
                  <Target className="how-icon" />
                </div>
                <h3 className="how-step-title">Take Smart Actions</h3>
                <p className="how-step-description">
                  Receive clear recommendations that help you save money and avoid financial mistakes
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="features-section">
        <div className="section-container">
          <h2 className="section-title">Core Features</h2>
          
          <div className="features-grid">
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon detect">
                  <AlertCircle className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Detect Hidden Subscriptions</h3>
                <p className="feature-description">
                  Automatically identify recurring charges and duplicates
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon alert">
                  <Bell className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Price Increase Alerts</h3>
                <p className="feature-description">
                  Know immediately when a bill quietly increases
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon interest">
                  <TrendingDown className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Interest Leakage Insights</h3>
                <p className="feature-description">
                  See when credit card usage could cost unnecessary interest
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon trial">
                  <Bell className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Trial-to-Paid Alerts</h3>
                <p className="feature-description">
                  Get notified before a free trial converts into a paid subscription so you can cancel before the charge happens
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon cancellation">
                  <Zap className="h-8 w-8" />
                </div>
                <h3 className="feature-title">AI-Assisted Subscription Cancellation</h3>
                <p className="feature-description">
                  Detects unused subscriptions, prepares cancellation messages when needed, and directs you to the fastest cancellation path
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon card">
                  <CreditCard className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Smart Card Guidance</h3>
                <p className="feature-description">
                  Know which credit card to use for each purchase to reduce interest risk and prevent high credit utilization
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon credit-guardian">
                  <Activity className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Credit Score Guardian</h3>
                <p className="feature-description">
                  Monitor card utilization and rebalance balances to protect your credit score and reduce long-term borrowing costs
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon balance">
                  <BarChart3 className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Balance Rebalancing Alerts</h3>
                <p className="feature-description">
                  If one credit card exceeds recommended utilization while another has unused limit, the system alerts you and suggests how to rebalance
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon tracker">
                  <FileText className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Financial & Life Admin Hub</h3>
                <p className="feature-description">
                  Track document expirations, insurance policies, and important records
                </p>
              </CardContent>
            </Card>
            
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon intelligence">
                  <Sparkles className="h-8 w-8" />
                </div>
                <h3 className="feature-title">Transaction-to-Action Intelligence</h3>
                <p className="feature-description">
                  Turn everyday financial activity into reminders or opportunities
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="who-section">
        <div className="section-container">
          <h2 className="section-title">Who This Is For</h2>
          
          <div className="who-grid">
            <Card className="who-card">
              <CardContent className="who-content">
                <Users className="who-icon" />
                <p className="who-description">Busy professionals who want to simplify finances</p>
              </CardContent>
            </Card>
            
            <Card className="who-card">
              <CardContent className="who-content">
                <Users className="who-icon" />
                <p className="who-description">Families managing multiple subscriptions and bills</p>
              </CardContent>
            </Card>
            
            <Card className="who-card">
              <CardContent className="who-content">
                <CreditCard className="who-icon" />
                <p className="who-description">People trying to reduce credit card interest</p>
              </CardContent>
            </Card>
            
            <Card className="who-card">
              <CardContent className="who-content">
                <DollarSign className="who-icon" />
                <p className="who-description">Anyone who wants to avoid hidden financial leaks</p>
              </CardContent>
            </Card>
            
            <Card className="who-card">
              <CardContent className="who-content">
                <CreditCard className="who-icon" />
                <p className="who-description">People with multiple credit cards or subscriptions</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What Makes This Different Section */}
      <section className="different-section">
        <div className="different-container">
          <h2 className="different-title">Why This Is Different</h2>
          
          <div className="different-content">
            <p className="different-intro">
              Most financial apps focus on budgeting and spending charts.
            </p>
            
            <p className="different-highlight">
              This system focuses on detecting financial leaks and recommending actions that save money or time.
            </p>
            
            <p className="different-explanation">
              Instead of asking you to manage everything manually, the system watches for problems and 
              alerts you only when something requires attention.
            </p>
          </div>
        </div>
      </section>

      {/* Example Week Section */}
      <section className="example-week-section">
        <div className="section-container">
          <h2 className="section-title">A Typical Week Using the App</h2>
          <p className="section-subtitle">See how the AI watches over your finances</p>
          
          <div className="week-timeline">
            <Card className="week-card">
              <CardContent className="week-content">
                <div className="week-day">
                  <Calendar className="week-icon" />
                  <span>Monday</span>
                </div>
                <p className="week-event">Your internet bill increased by $9. The system flags the change.</p>
              </CardContent>
            </Card>
            
            <Card className="week-card">
              <CardContent className="week-content">
                <div className="week-day">
                  <Calendar className="week-icon" />
                  <span>Wednesday</span>
                </div>
                <p className="week-event">A free trial converted into a paid subscription.</p>
              </CardContent>
            </Card>
            
            <Card className="week-card">
              <CardContent className="week-content">
                <div className="week-day">
                  <Calendar className="week-icon" />
                  <span>Friday</span>
                </div>
                <p className="week-event">One credit card crosses 35% utilization while another has unused limit.</p>
              </CardContent>
            </Card>
            
            <Card className="week-card">
              <CardContent className="week-content">
                <div className="week-day">
                  <Calendar className="week-icon" />
                  <span>Sunday</span>
                </div>
                <p className="week-event">You receive a summary of potential savings opportunities.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Product Preview Section */}
      <section className="preview-section">
        <div className="section-container">
          <h2 className="section-title">Product Preview</h2>
          <p className="section-subtitle">See how AI insights appear in your dashboard</p>
          
          <Card className="preview-dashboard">
            <CardContent className="preview-content">
              <div className="preview-header">
                <h3 className="preview-title">Recent Insights</h3>
                <span className="preview-badge">Live</span>
              </div>
              
              <div className="preview-insights">
                <div className="preview-insight">
                  <div className="preview-dot warning"></div>
                  <div className="preview-text">
                    <p className="preview-message">You are paying for two music services</p>
                    <span className="preview-action">Review subscriptions</span>
                  </div>
                  <div className="preview-savings">-$12/mo</div>
                </div>
                
                <div className="preview-insight">
                  <div className="preview-dot alert"></div>
                  <div className="preview-text">
                    <p className="preview-message">Your telecom bill increased this month</p>
                    <span className="preview-action">Compare plans</span>
                  </div>
                  <div className="preview-savings">+15%</div>
                </div>
                
                <div className="preview-insight">
                  <div className="preview-dot success"></div>
                  <div className="preview-text">
                    <p className="preview-message">Your dentist visit may qualify for insurance claim</p>
                    <span className="preview-action">Submit claim</span>
                  </div>
                  <div className="preview-savings">$180</div>
                </div>
                
                <div className="preview-insight">
                  <div className="preview-dot info"></div>
                  <div className="preview-text">
                    <p className="preview-message">Your passport expires in 6 months</p>
                    <span className="preview-action">Set reminder</span>
                  </div>
                  <div className="preview-savings">—</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust and Privacy Section - Expanded */}
      <section className="trust-section">
        <div className="section-container">
          <h2 className="section-title">Built for Trust and Privacy</h2>
          <p className="section-description">
            Financial tools require a high level of trust.
          </p>
          <p className="section-description">
            This platform is designed with a privacy-first approach to ensure your financial data is secure and under your control.
          </p>
          
          {/* Trust Cards Grid */}
          <div className="trust-grid">
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Eye className="trust-icon" />
                </div>
                <h3 className="trust-card-title">Read-only Access</h3>
                <p className="trust-card-description">
                  We only analyze your financial data. We cannot move or modify your money.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Shield className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Money Movement</h3>
                <p className="trust-card-description">
                  The system does not initiate transactions or transfers.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Lock className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Data Selling</h3>
                <p className="trust-card-description">
                  Your personal financial data is never sold or shared with third parties.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Zap className="trust-icon" />
                </div>
                <h3 className="trust-card-title">On-device Processing</h3>
                <p className="trust-card-description">
                  Sensitive insights are generated locally on your device to minimize data exposure (where possible).
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="trust-footer">
            <Shield className="trust-footer-icon" />
            <p className="trust-footer-text">Your financial data belongs to you — always.</p>
          </div>
        </div>
      </section>

      {/* Future Vision Section */}
      <section className="vision-section">
        <div className="vision-container">
          <Lightbulb className="vision-icon-large" />
          <h2 className="vision-title">Where This Is Going</h2>
          <p className="vision-description">
            The long-term vision is a system that quietly watches over your financial and administrative life.
          </p>
          <p className="vision-detail">
            Instead of constantly managing subscriptions, bills, documents, and reminders, you simply approve 
            recommendations when needed.
          </p>
          <p className="vision-conclusion">
            Life admin becomes simpler and less stressful.
          </p>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="demo-section">
        <div className="section-container">
          <div className="demo-content">
            <Sparkles className="demo-icon" />
            <h2 className="demo-title">See How It Works</h2>
            <p className="demo-description">
              Explore the interactive prototype and see how AI detects financial leaks and highlights savings opportunities.
            </p>
            <Button 
              size="lg" 
              className="demo-button"
              onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
            >
              Explore the Interactive Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof-section">
        <div className="section-container">
          <h2 className="section-title">Why Early Users Are Joining</h2>
          
          <div className="testimonials-grid">
            <Card className="testimonial-card">
              <CardContent className="testimonial-content">
                <p className="testimonial-quote">
                  "Finally something that focuses on saving money instead of charts."
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    <Users className="avatar-icon" />
                  </div>
                  <div className="author-info">
                    <p className="author-name">Early Tester</p>
                    <p className="author-role">Beta User</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="testimonial-card">
              <CardContent className="testimonial-content">
                <p className="testimonial-quote">
                  "Credit utilization monitoring alone is worth it."
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    <Users className="avatar-icon" />
                  </div>
                  <div className="author-info">
                    <p className="author-name">Early Tester</p>
                    <p className="author-role">Beta User</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="testimonial-card">
              <CardContent className="testimonial-content">
                <p className="testimonial-quote">
                  "This would have saved me hundreds last year."
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    <Users className="avatar-icon" />
                  </div>
                  <div className="author-info">
                    <p className="author-name">Early Tester</p>
                    <p className="author-role">Beta User</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section className="waitlist-section" id="waitlist-section">
        <div className="waitlist-container">
          <div className="waitlist-content">
            <h2 className="waitlist-title">Join Early Access</h2>
            <p className="waitlist-description">
              We are inviting early users to help shape the product.
            </p>
            
            <p className="waitlist-incentive">
              Early users will help shape the product and get priority access at launch.
            </p>
            
            <div className="waitlist-counter">
              <Users className="counter-icon" />
              <p className="counter-text">
                <strong>{waitlistCount.toLocaleString()}</strong> people joined the waitlist
              </p>
            </div>
            
            <div className="waitlist-benefits">
              <div className="benefit-item">
                <CheckCircle2 className="benefit-icon" />
                <span>Test the prototype</span>
              </div>
              <div className="benefit-item">
                <CheckCircle2 className="benefit-icon" />
                <span>Influence future features</span>
              </div>
              <div className="benefit-item">
                <CheckCircle2 className="benefit-icon" />
                <span>Get priority access at launch</span>
              </div>
            </div>
          </div>
          
          <Card className="waitlist-card">
            <CardContent className="waitlist-form-container">
              {submitSuccess ? (
                <div className="success-message">
                  <CheckCircle2 className="success-icon" />
                  <h3>{getPersonalizedMessage(priorityProblem).title}</h3>
                  <p>{getPersonalizedMessage(priorityProblem).message}</p>
                </div>
              ) : (
                <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email *</label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="country" className="form-label">Country (optional)</label>
                    <Input
                      id="country"
                      type="text"
                      placeholder="e.g., United States"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group priority-problem-group">
                    <label className="form-label form-label-required">
                      What would make you sign up today? *
                    </label>
                    <p className="form-sublabel">
                      Select the one problem you would most like solved automatically.
                    </p>
                    <div className="radio-options">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="priorityProblem"
                          value="trial_reminder"
                          checked={priorityProblem === 'trial_reminder'}
                          onChange={(e) => setPriorityProblem(e.target.value)}
                          required
                        />
                        <span className="radio-label">Never forget a free trial again</span>
                      </label>
                      
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="priorityProblem"
                          value="bill_increase"
                          checked={priorityProblem === 'bill_increase'}
                          onChange={(e) => setPriorityProblem(e.target.value)}
                          required
                        />
                        <span className="radio-label">Catch bill increases automatically</span>
                      </label>
                      
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="priorityProblem"
                          value="credit_protection"
                          checked={priorityProblem === 'credit_protection'}
                          onChange={(e) => setPriorityProblem(e.target.value)}
                          required
                        />
                        <span className="radio-label">Protect my credit score automatically</span>
                      </label>
                      
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="priorityProblem"
                          value="reduce_interest"
                          checked={priorityProblem === 'reduce_interest'}
                          onChange={(e) => setPriorityProblem(e.target.value)}
                          required
                        />
                        <span className="radio-label">Reduce unnecessary credit card interest</span>
                      </label>
                      
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="priorityProblem"
                          value="find_subscriptions"
                          checked={priorityProblem === 'find_subscriptions'}
                          onChange={(e) => setPriorityProblem(e.target.value)}
                          required
                        />
                        <span className="radio-label">Find hidden subscriptions and recurring charges</span>
                      </label>
                      
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="priorityProblem"
                          value="continuous_monitoring"
                          checked={priorityProblem === 'continuous_monitoring'}
                          onChange={(e) => setPriorityProblem(e.target.value)}
                          required
                        />
                        <span className="radio-label">Continuously monitor my finances for money leaks</span>
                      </label>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Joining...' : 'Join the Waitlist'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-section">
        <div className="final-container">
          <h2 className="final-title">Let AI Watch for Financial Leaks So You Don't Have To</h2>
          <p className="final-subtitle">
            Life is complicated. Managing it shouldn't be.
          </p>
          <div className="final-cta">
            <Button 
              size="lg" 
              className="cta-primary"
              onClick={() => {
                document.getElementById('waitlist-section').scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Join Early Access
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="cta-secondary"
              onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
            >
              Explore Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Feedback Popup */}
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default LandingPage;
